import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Bot,
  CreditCard,
  Filter,
  Lock,
  PieChart as PieChartIcon,
  Users,
} from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";
import { apiService } from "../services/api";
import type {
  BarChartData,
  CopilotWidgetFilters,
  FilterOptionsData,
  MultiBarChartData,
  PieChartData,
  SingleValueData,
} from "../types";
import axios from "axios";

const PIE_COLORS = [
  "#0f766e",
  "#1d4ed8",
  "#ca8a04",
  "#be123c",
  "#7c3aed",
  "#0369a1",
  "#b45309",
  "#15803d",
];

function getPieColor(index: number): string {
  return PIE_COLORS[index % PIE_COLORS.length] ?? "#0f766e";
}

function useWidget<T>(widgetId: string, filters?: CopilotWidgetFilters) {
  return useQuery(
    ["copilot-widget", widgetId, filters],
    () => apiService.getCopilotWidget(widgetId, filters),
    {
      refetchOnWindowFocus: false,
      select: (res) => res?.data?.data as T,
      retry: (failureCount, error) => {
        if (axios.isAxiosError(error) && error.response?.status === 403)
          return false;
        return failureCount < 3;
      },
    },
  );
}

const formatShortDay = (value: string) => {
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

export default function GithubCopilotDashboardPage() {
  const [selectedBA, setSelectedBA] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedPA, setSelectedPA] = useState("");

  const filters = useMemo<CopilotWidgetFilters>(() => {
    const f: CopilotWidgetFilters = {};
    if (selectedBA) f.businessAreaName = selectedBA;
    if (selectedCompany) f.companyName = selectedCompany;
    if (selectedPA) f.productAreaName = selectedPA;
    return f;
  }, [selectedBA, selectedCompany, selectedPA]);

  const {
    data: filterOptions,
    isLoading: filterLoading,
    error: filterError,
  } = useWidget<FilterOptionsData>("filter-options");
  const { data: businessLicenses } =
    useWidget<SingleValueData>("business-licenses");
  const { data: enterpriseLicenses } = useWidget<SingleValueData>(
    "enterprise-licenses",
  );
  const { data: latestDau } = useWidget<SingleValueData>("latest-dau", filters);
  const { data: baPie } = useWidget<PieChartData>("distribution-by-ba");
  const { data: companyPie } = useWidget<PieChartData>(
    "distribution-by-company",
    filters,
  );
  const { data: dauBar } = useWidget<BarChartData>("dau", filters);
  const { data: baTrend } = useWidget<MultiBarChartData>("ba-active-users");

  const businessAreas = filterOptions?.businessAreas ?? [];
  const companiesByBA = filterOptions?.companiesByBusinessArea ?? {};
  const productAreasByBA = filterOptions?.productAreasByBusinessArea ?? {};
  const productAreasByCompany = filterOptions?.productAreasByCompany ?? {};

  const availableCompanies = useMemo(() => {
    if (!selectedBA) return filterOptions?.companies ?? [];
    return companiesByBA[selectedBA] ?? [];
  }, [filterOptions, companiesByBA, selectedBA]);

  const availableProductAreas = useMemo(() => {
    if (selectedCompany) return productAreasByCompany[selectedCompany] ?? [];
    if (selectedBA) return productAreasByBA[selectedBA] ?? [];
    return filterOptions?.productAreas ?? [];
  }, [
    filterOptions,
    productAreasByBA,
    productAreasByCompany,
    selectedBA,
    selectedCompany,
  ]);

  useEffect(() => {
    if (selectedCompany && !availableCompanies.includes(selectedCompany))
      setSelectedCompany("");
  }, [availableCompanies, selectedCompany]);

  useEffect(() => {
    if (selectedPA && !availableProductAreas.includes(selectedPA))
      setSelectedPA("");
  }, [availableProductAreas, selectedPA]);

  const dauItems = dauBar?.items ?? [];
  const trailing30Avg =
    dauItems.length > 0
      ? Math.round(
          (dauItems.reduce((s, i) => s + i.value, 0) / dauItems.length) * 10,
        ) / 10
      : 0;

  const filterLabel = [selectedBA, selectedCompany, selectedPA]
    .filter(Boolean)
    .join(" › ");

  const baPieItems = (baPie?.items ?? []).filter((i) => i.value > 0);
  const companyPieItems = (companyPie?.items ?? []).filter((i) => i.value > 0);

  const baTrendSeries = baTrend?.series ?? [];
  const baTrendData = useMemo(() => {
    if (!baTrend) return [];
    return baTrend.items.map((item) => {
      const row: Record<string, string | number> = { label: item.label };
      baTrendSeries.forEach((s) => {
        row[s] = item.values[s] ?? 0;
      });
      return row;
    });
  }, [baTrend, baTrendSeries]);

  if (filterLoading) return <LoadingSpinner />;

  if (
    filterError &&
    axios.isAxiosError(filterError) &&
    filterError.response?.status === 403
  ) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Lock className="h-12 w-12 text-gray-400 dark:text-gray-500" />
        <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
          Access Denied
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          You do not have permission to view this dashboard.{" "}
          <a
            href="https://teams.microsoft.com/l/channel/19%3AMb81OGCp1Yotr2FlzHxFv6qSh6uvntf5KbTcYOWbb9g1%40thread.tacv2/General?groupId=fff5bd60-10ea-4534-81c9-d0c9f95848ac&tenantId=e11cbe9c-f680-44b9-9d42-d705f740b888"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline"
          >
            Request access via Teams
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          GitHub Copilot
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Daily active usage from Copilot activity joined with SISH user
          metadata.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
              <Filter className="h-5 w-5 text-sky-500" />
              Global Filters
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Applies to filtered widgets.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="flex flex-col text-sm font-medium text-gray-700 dark:text-gray-300">
              Business area
              <select
                value={selectedBA}
                onChange={(e) => setSelectedBA(e.target.value)}
                className="mt-1 min-w-56 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              >
                <option value="">All business areas</option>
                {businessAreas.map((ba) => (
                  <option key={ba} value={ba}>
                    {ba}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-sm font-medium text-gray-700 dark:text-gray-300">
              Company
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={!selectedBA}
                className="mt-1 min-w-56 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              >
                <option value="">All companies</option>
                {availableCompanies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col text-sm font-medium text-gray-700 dark:text-gray-300">
              Product area
              <select
                value={selectedPA}
                onChange={(e) => setSelectedPA(e.target.value)}
                disabled={!selectedCompany}
                className="mt-1 min-w-56 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              >
                <option value="">All product areas</option>
                {availableProductAreas.map((pa) => (
                  <option key={pa} value={pa}>
                    {pa}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Business Licenses
            </span>
            <CreditCard className="h-5 w-5 text-violet-500" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-gray-900 dark:text-gray-100">
            {businessLicenses?.value ?? 0}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Assigned Copilot Business seats
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Enterprise Licenses
            </span>
            <CreditCard className="h-5 w-5 text-amber-500" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-gray-900 dark:text-gray-100">
            {enterpriseLicenses?.value ?? 0}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Assigned Copilot Enterprise seats
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Latest DAU{filterLabel ? ` (${filterLabel})` : ""}
            </span>
            <Bot className="h-5 w-5 text-emerald-500" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-gray-900 dark:text-gray-100">
            {latestDau?.value ?? 0}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {latestDau?.label || "No data"}
          </p>
        </div>
      </div>

      {/* <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Raw Copilot Users</span>
            <Users className="h-5 w-5 text-blue-500" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-gray-900 dark:text-gray-100">{rawUsers?.value ?? 0}</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Distinct users in copilot_user_dailies</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Mapped Users{filterLabel ? ` (${filterLabel})` : ""}
            </span>
            <Users className="h-5 w-5 text-cyan-500" />
          </div>
          <p className="mt-3 text-3xl font-semibold text-gray-900 dark:text-gray-100">{mappedUsers?.value ?? 0}</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Distinct users matched to SISH{filterLabel ? " (filtered)" : ""}</p>
        </div>
      </div> */}

      <div
        className={`grid grid-cols-1 gap-5 ${selectedBA ? "xl:grid-cols-3" : "xl:grid-cols-2"}`}
      >
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              By Business Area
            </h2>
            <PieChartIcon className="h-5 w-5 text-amber-600" />
          </div>
          <div className="mt-6 h-56">
            {baPieItems.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                No data
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={baPieItems}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={65}
                    outerRadius={110}
                    paddingAngle={2}
                  >
                    {baPieItems.map((e, i) => (
                      <Cell key={e.name} fill={getPieColor(i)} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, _n, item) => [
                      v,
                      item?.payload?.name ?? "BA",
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          {baPieItems.length > 0 && (
            <ul className="mt-4 space-y-1">
              {baPieItems.slice(0, 5).map((item, i) => (
                <li
                  key={item.name}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ backgroundColor: getPieColor(i) }}
                    />
                    <span className="text-gray-700 dark:text-gray-300">
                      {item.name}
                    </span>
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {item.value}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selectedBA && (
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  By Company ({selectedBA})
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Active users in the last 7 days
                </p>
              </div>
              <PieChartIcon className="h-5 w-5 text-sky-600" />
            </div>
            <div className="mt-6 h-56">
              {companyPieItems.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-500">
                  No data
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={companyPieItems}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={65}
                      outerRadius={110}
                      paddingAngle={2}
                    >
                      {companyPieItems.map((e, i) => (
                        <Cell key={e.name} fill={getPieColor(i)} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, _n, item) => [
                        v,
                        item?.payload?.name ?? "Company",
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            {companyPieItems.length > 0 && (
              <ul className="mt-4 space-y-1">
                {companyPieItems.slice(0, 5).map((item, i) => (
                  <li
                    key={item.name}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block h-3 w-3 rounded-full"
                        style={{ backgroundColor: getPieColor(i) }}
                      />
                      <span className="text-gray-700 dark:text-gray-300">
                        {item.name}
                      </span>
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {item.value}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Daily Active Users
            </h2>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              30 day avg: {trailing30Avg}
            </div>
          </div>
          <div className="mt-6 h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dauItems}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
                <XAxis
                  dataKey="label"
                  minTickGap={20}
                  tickFormatter={formatShortDay}
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  labelFormatter={(l) =>
                    typeof l === "string" ? formatShortDay(l) : l
                  }
                />
                <Bar
                  dataKey="value"
                  fill="#2563eb"
                  name="DAU"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Business Area Active Users
          </h2>
          <Users className="h-5 w-5 text-orange-500" />
        </div>
        <div className="mt-6 h-96">
          {baTrendData.length === 0 || baTrendSeries.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              No data
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={baTrendData} margin={{ left: 8, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
                <XAxis
                  dataKey="label"
                  minTickGap={20}
                  tickFormatter={formatShortDay}
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  labelFormatter={(l) =>
                    typeof l === "string" ? formatShortDay(l) : l
                  }
                />
                <Legend />
                {baTrendSeries.map((s, i) => (
                  <Bar
                    key={s}
                    dataKey={s}
                    fill={getPieColor(i)}
                    name={s}
                    radius={[3, 3, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
